import React from 'react';
import { useVariantOptionState } from '../context/VariantOptionContext';
import { useVariantSelectionState } from '../context/VariantSelectionContext';
import type DecisionType from '@modules/product/type/decision-type';
import type Variant from '@modules/product/type/variant';
import type Attribute from '@modules/product/type/attribute';

interface OptionDecisionType {
    ID: number;
    attributeID: number;
}

const getOptionAttributeID = (
    optionID: number,
    attributeOptionMap: Map<number, Attribute>,
) => {
    return attributeOptionMap.get(optionID)?.ID || 0;
};

const findMostSatisfiedVariant = (
    variants: Variant[],
    decision: DecisionType,
    option: OptionDecisionType | null,
    sizeID: number | null,
    attributeOptionMap: Map<number, Attribute>,
    canonicalOptionIds?: Map<number, number>,
): Variant | null => {
    // B2: compare option IDs through their canonical twin so that variants
    // referencing a duplicate option record (same label, different ID) are
    // still reachable after display-level deduplication.
    const canonical = (id: number) => canonicalOptionIds?.get(id) ?? id;

    const availableVariants = variants
        .filter((v) => {
            if (option) {
                return option.ID === 0
                    ? v.Options.every(
                          (opt) => opt.AttributeID !== option.attributeID,
                      )
                    : v.Options.some(
                          (o) => canonical(o.ID) === canonical(option.ID),
                      );
            } else if (sizeID !== null) {
                return sizeID === 0 ? !v.Size : v.Size?.ID === sizeID;
            } else {
                return true;
            }
        })
        .sort((a, b) => {
            return a.Options.length - b.Options.length;
        });

    const perfectVariant = availableVariants
        .map((v) => {
            let score = 0;

            const neededOptionGroups = Object.keys(decision.options)
                .filter(
                    (attributeID: string) =>
                        !option || parseInt(attributeID) !== option.attributeID,
                )
                .map((attributeID: string) => ({
                    attributeID: parseInt(attributeID),
                    optionID: decision.options[parseInt(attributeID)],
                }));

            neededOptionGroups.every(({ attributeID, optionID }) => {
                if (
                    optionID === 0 &&
                    !v.Options.every(
                        (opt) =>
                            getOptionAttributeID(opt.ID, attributeOptionMap) !==
                            attributeID,
                    )
                ) {
                    score++;
                    return true;
                }
                if (
                    optionID != null &&
                    v.Options.some(
                        (opt) => canonical(opt.ID) === canonical(optionID),
                    )
                ) {
                    score++;
                    return true;
                }
                return false;
            });
            const neededSizeID = sizeID !== null ? sizeID : decision.size;
            const sizeSatisfies = neededSizeID
                ? v.Size?.ID === neededSizeID
                : v.Size === null;

            if (sizeSatisfies) {
                score++;
            }

            return { score, variant: v };
        })
        .sort((a, b) => {
            return b.score - a.score;
        })
        .map((g) => g.variant)[0];

    return perfectVariant || (availableVariants[0] ?? null);
};

/** The decision (chip/size selection) implied by a variant. Option IDs are
 *  stored as their canonical twin so the decision always matches the deduped
 *  display options (selection highlight, availability, enquiry labels). */
const deriveDecision = (
    variant: Variant | null,
    attributeOptionMap: Map<number, Attribute>,
    canonicalOptionIds?: Map<number, number>,
): DecisionType => {
    if (!variant) {
        return { options: {}, size: null };
    }
    const options: { [key: number]: number } = {};
    variant.Options.forEach((opt) => {
        options[getOptionAttributeID(opt.ID, attributeOptionMap)] =
            canonicalOptionIds?.get(opt.ID) ?? opt.ID;
    });
    return { options, size: variant.Size?.ID || null };
};

const useVariantDecision = (variants: Variant[]) => {
    const { selectedVariant, setSelectedVariant } = useVariantSelectionState();
    const { decision, setDecision, attributeOptionMap, canonicalOptionIds } =
        useVariantOptionState();

    // Set the variant AND its decision together so the selector highlight, image
    // and price never disagree for a frame (the effect below still syncs decision
    // when selectedVariant changes from outside a pick, e.g. initial load).
    const applyVariant = (variant: Variant | null) => {
        setSelectedVariant(variant);
        setDecision(
            deriveDecision(variant, attributeOptionMap, canonicalOptionIds),
        );
    };

    /**
     * A pick that matches no variant leaves the selection alone.
     *
     * `findMostSatisfiedVariant` returns null when nothing pairs with the pick
     * — a product carrying options but no variants, or an option record no
     * variant references. Handing that null to `applyVariant` cleared the whole
     * decision: the chip the reader had just pressed came back unselected and
     * struck through, the colour swatch went to its unavailable slash, and
     * everything keyed on having a variant (the price, the stock line, Add to
     * Cart, and every spec row filtered on having a value) disappeared with it.
     *
     * A pick is a request for a configuration, so the worst honest answer is
     * that nothing changes. Deselecting was never one of the options a reader
     * was offered.
     */
    const applyPick = (variant: Variant | null) => {
        if (!variant) return;
        applyVariant(variant);
    };

    const pickOption = (option: OptionDecisionType) => {
        applyPick(
            findMostSatisfiedVariant(
                variants,
                decision,
                option,
                null,
                attributeOptionMap,
                canonicalOptionIds,
            ),
        );
    };

    const pickSize = (sizeID: number | null) => {
        applyPick(
            findMostSatisfiedVariant(
                variants,
                decision,
                null,
                sizeID,
                attributeOptionMap,
                canonicalOptionIds,
            ),
        );
    };

    React.useEffect(() => {
        setDecision(
            deriveDecision(
                selectedVariant,
                attributeOptionMap,
                canonicalOptionIds,
            ),
        );
    }, [selectedVariant, setDecision, attributeOptionMap]);

    return {
        pickOption,
        pickSize,
    };
};

export { useVariantDecision };
